import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { useEditKnowledgeBaseNameMutation } from 'src/services';
import Input from 'src/components/Input/Input';
import { useEffect } from 'react';
import { clsx } from 'clsx';
import { CheckIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CheckIcon/CheckIcon';
import { AddItemIconDisabled } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIconDisabled';
import { useNotification } from 'src/hooks/useNotification';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';

// Renames the knowledge base being created when the name field loses focus.
export const CreateNewKBInput = ({
  item,
}: {
  item?: (Pick<KnowledgeBase, 'id'> & Partial<Pick<KnowledgeBase, 'name'>>) | null;
}) => {
  const notification = useNotification();
  const [editKnowledgeBase, { isLoading: editKnowledgeBaseIsLoading }] =
    useEditKnowledgeBaseNameMutation();

  useEffect(() => {
    if (item) {
      setValue('name', item.name);
    }
  }, [item]);

  const { handleSubmit, control, setValue } = useForm({
    defaultValues: {
      name: item ? item.name : '',
    },
    resolver: yupResolver(
      object({
        name: stringScheme({ required: true }).label('DataBase Name'),
      }),
    ),
  });

  const submit = ({ name }: { name: string }) => {
    if (name && item?.id && name !== item.name) {
      editKnowledgeBase({
        name: name,
        knowledge_base_id: item?.id,
      }).then((editKnowledgeBaseResult) => {
        if (!('error' in editKnowledgeBaseResult)) {
          notification.success('Knowledge Base was successfully updated!');
        }
      });
    }
  };

  const onBlur = () => {
    handleSubmit(submit)();
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={'w-full flex flex-row items-start gap-5'}>
      <div className={'pt-5'}>
        <CheckIcon className={'invisible'} />
      </div>
      <div className={'w-full flex flex-col gap-1.5'}>
        <Controller
          control={control}
          name="name"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Knowledge base name:'}
              placeholder={'AI Knowledge Base'}
              className={clsx(
                'border border-[#B8C1C633]',
                editKnowledgeBaseIsLoading && 'blur-sm animate-pulse pointer-events-none',
              )}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              onBlur={onBlur}
              readonly={editKnowledgeBaseIsLoading}
            />
          )}
        />
      </div>
      <div className={clsx('invisible')}>
        <AddItemIconDisabled />
      </div>
    </form>
  );
};
